/**
 * `useUploadArtifacts` mutation hook.
 *
 * Drives the per-chip upload pipeline for the Add Artifacts → Upload sub-flow.
 * Each chip walks the 3-step upload chain
 * (`requestProjectFileUpload` → `uploadToS3` → `confirmProjectFileUpload`)
 * with `purpose: 'artifact'` so the row lands in the merged Library list
 * (project_artifact UNION project_file WHERE purpose='artifact').
 *
 * Concurrency: chips are processed in batches of `UPLOAD_CONCURRENCY = 3` via
 * `Promise.allSettled`. All chips are attempted regardless of per-file
 * failures — diverges from create-project's all-or-nothing because the
 * repeat-upload context risks duplicate-on-retry. Per-chip outcomes bubble
 * to the caller via `onPerFile(id, status, errorMessage?)` so the UploadView
 * can update chip styling mid-flight.
 *
 * After the final batch settles, invalidates list queries via
 * `PROJECT_ARTIFACT_QUERY_KEYS.listsForProject(projectId)` so the Library
 * grid refreshes across all filter/sort/page combinations without
 * unnecessarily clearing attachedIds or detail queries, then fires
 * aggregate success/error toasts.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { PROJECT_ARTIFACT_QUERY_KEYS } from '@/entities/project-artifact';
import { confirmProjectFileUpload, requestProjectFileUpload } from '@/entities/project-file';
import { uploadToS3 } from '@/shared/api/upload-to-s3';
import { notificationService } from '@/lib/services/notification.service';
import { sanitizeErrorMessage } from '@/lib/upload-queue/error-message';
import { isUserAbort } from '@/lib/upload-queue/is-user-abort';
import { enqueueUpload } from '@/lib/upload-queue/upload-queue-bindings';
import { logger } from '@/lib/utils/logger';

/**
 * Concurrent upload slots — empirical balance:
 * - 1 underutilises the browser's HTTP/2 multiplexing budget and feels slow
 *   on small queues
 * - too high (≥ 5) starves the main thread under file-read backpressure and
 *   risks the user's own keystrokes / dropzone interactions stuttering
 *   mid-flight
 * - 3 is the sweet spot empirically — keeps Add → done latency tight on
 *   typical 5-file queues without visibly competing with the UI
 *
 * Exported so test fixtures and call sites stay aligned if the value moves.
 */
const UPLOAD_CONCURRENCY = 3;

export interface ChipFile {
  id: string;
  file: File;
}

export type ChipStatus = 'uploading' | 'done' | 'error' | 'cancelled';

export type OnPerFile = (id: string, status: ChipStatus, errorMessage?: string) => void;

interface UseUploadArtifactsOptions {
  projectId: string;
  onPerFile: OnPerFile;
}

interface UploadArtifactsResult {
  done: number;
  errored: number;
}

export function useUploadArtifacts(options: UseUploadArtifactsOptions) {
  const { t } = useTranslation('projects');
  const queryClient = useQueryClient();
  const { projectId, onPerFile } = options;

  return useMutation<UploadArtifactsResult, Error, ChipFile[]>({
    mutationFn: async (chips: ChipFile[]) => {
      const runOne = async (chip: ChipFile): Promise<'done' | 'error' | 'cancelled'> => {
        onPerFile(chip.id, 'uploading');

        // Enqueue AFTER onPerFile('uploading') and BEFORE the request step
        // so the global tray reflects the chip in start order and
        // concurrency=3 is preserved (each runOne enqueues exactly once).
        // Cross-feature value edge is inverted via the `lib/upload-queue`
        // binding seam — the wire never imports from `@/features/upload-queue`
        // directly.
        const handle = enqueueUpload({ file: chip.file, source: 'library', projectId });

        try {
          const uploadResponse = await requestProjectFileUpload(projectId, {
            fileName: chip.file.name,
            fileSize: chip.file.size,
            contentType: chip.file.type,
            purpose: 'artifact',
          });

          await uploadToS3(
            uploadResponse.uploadUrl,
            uploadResponse.uploadFields,
            chip.file,
            handle.abortSignal,
            handle.onProgress,
          );

          await confirmProjectFileUpload(
            projectId,
            uploadResponse.fileId,
            uploadResponse.versionId,
          );

          handle.markSuccess();
          onPerFile(chip.id, 'done');
          return 'done';
        } catch (err) {
          // User-cancel race: queue.cancel() already transitioned the item
          // to 'cancelled'. Do NOT call markFailed — the terminal-state
          // guard would no-op it anyway, but skipping the call keeps intent
          // clear AND prevents the aggregate `errored` tally / "N uploads
          // failed" toast from blaming the user for a deliberate cancel.
          if (isUserAbort(err)) {
            onPerFile(chip.id, 'cancelled');
            return 'cancelled';
          }
          // eslint-disable-next-line i18next/no-literal-string -- pre-sanitize placeholder; `sanitizeErrorMessage` returns the translated fallback
          const raw = err instanceof Error ? err.message : 'Upload failed';
          logger.error('[manage-project-artifacts] upload failed', {
            chipId: chip.id,
            file: chip.file.name,
            raw,
            err,
          });
          const sanitized = sanitizeErrorMessage(raw);
          handle.markFailed(sanitized);
          onPerFile(chip.id, 'error', sanitized);
          return 'error';
        }
      };

      let done = 0;
      let errored = 0;

      for (let i = 0; i < chips.length; i += UPLOAD_CONCURRENCY) {
        const batch = chips.slice(i, i + UPLOAD_CONCURRENCY);
        const results = await Promise.allSettled(batch.map(runOne));

        for (const result of results) {
          // `runOne` swallows its own errors and resolves to 'done' |
          // 'error' | 'cancelled', so a `rejected` outcome should be
          // impossible here — treat as `error` to keep the aggregate
          // counts honest if the invariant breaks (e.g. `onPerFile`
          // throws). The chip status was already set inside `runOne`.
          //
          // 'cancelled' is intentionally NOT tallied here — the per-chip
          // `'cancelled'` callback (see runOne) is the existing surface.
          // A "N cancelled" toast would be a real feature with its own
          // UX spec; a placeholder tally suppressed via `void cancelled`
          // would just be dead code.
          if (result.status === 'fulfilled') {
            if (result.value === 'done') {
              done += 1;
            } else if (result.value === 'error') {
              errored += 1;
            }
            // cancelled: no-op aggregate; per-chip status already fired
          } else {
            errored += 1;
          }
        }
      }

      return { done, errored };
    },
    onSuccess: ({ done, errored }) => {
      // Skip the invalidation when nothing committed — cache-busting on an
      // all-errored batch just churns the project artifact subtree for no
      // reason. (Reviewer F5.)
      if (done > 0) {
        // Invalidate only list queries (all filter/sort/page combos) without
        // clearing attachedIds or detail queries unnecessarily.
        void queryClient.invalidateQueries({
          queryKey: PROJECT_ARTIFACT_QUERY_KEYS.listsForProject(projectId),
        });
        notificationService.showSuccess(
          t('manageProjectArtifacts.useUploadArtifacts.artifactsAdded', { count: done }),
        );
      }
      if (errored > 0) {
        notificationService.showError(
          t('manageProjectArtifacts.useUploadArtifacts.uploadsFailed', { count: errored }),
        );
      }
    },
  });
}
